// Tiny inline keybinding helper — used on /dev/mfa to bind 1/2/3/4 keys to
// the four demo buttons. We avoid pulling @tanstack/react-hotkeys here to
// keep the dep surface small (the prompt allows either; this is the chosen
// path). Single-key bindings only; ignore presses while focus is in an
// input/textarea so users can still type.

import { useEffect } from "react";

export type KeyMap = Record<string, () => void>;

function isTextEntry(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return (
    tag === "INPUT" ||
    tag === "TEXTAREA" ||
    tag === "SELECT" ||
    target.isContentEditable
  );
}

export function useHotkeys(map: KeyMap, enabled = true) {
  useEffect(() => {
    if (!enabled) return;
    function onKeyDown(ev: KeyboardEvent) {
      if (ev.metaKey || ev.ctrlKey || ev.altKey) return;
      if (isTextEntry(ev.target)) return;
      const handler = map[ev.key];
      if (handler) {
        ev.preventDefault();
        handler();
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [map, enabled]);
}
